import { endOfDay, isAfter } from 'date-fns';
import { Form, Formik, useField } from 'formik';

import { Button } from '../../../cdl/Button/Button';
import { Callout } from '../../../cdl/Callout/Callout';
import { Modal } from '../../../cdl/Modal/Modal';
import { Text } from '../../../cdl/Text/Text.tsx';
import { FormikTimestampDatePicker } from '../../../common/form-elements/formik/FormikTimestampDatePicker';
import { FormikFormattedNumberInput } from '../../../common/FormattedNumberInput/FormikFormattedNumberInput';
import { localStartOfDayAsUtcIso } from '../../../common/helpers/dateFns.helper';
import { formatNumber } from '../../../common/helpers/formatNumber.helper';
import { translate } from '../../../common/helpers/translate.helper';
import { Characters } from '../../../common/helpers/unicode.helper';
import { useEnums } from '../../../common/hooks/useEnums';
import { IconInfoCircle } from '../../../common/icons/cdl/InfoCircle';
import { Box } from '../../../common/ui/Box';

const FutureMeasurementDateCallout = () => {
    const [field] = useField('dateMeasured');

    if (field.value && isAfter(new Date(field.value), endOfDay(new Date()))) {
        return (
            <Box marginBottom={8}>
                <Callout
                    variant="attention"
                    description={translate('vessel.insertRemainingOnBoardPopup.dateMeasuredInFuture')}
                    Icon={IconInfoCircle}
                />
            </Box>
        );
    }

    return null;
};

export const InsertRemainingOnBoardPopup = ({ tanks, ...props }) => {
    const { getHumanReadableValue } = useEnums();

    const initialValues = {
        // Set dateMeasured to the users current date, but at the start of the day in UTC.
        // With the format we remove the timezone of the user.
        dateMeasured: new Date(localStartOfDayAsUtcIso(new Date())),
    };

    return (
        <Modal isOpen={props.open} onDismiss={props.onDismiss}>
            <Box display="flex" flexDirection="column" rowGap={16} padding={24}>
                <Text variant="title" as="h3">
                    {translate('vessel.insertRemainingOnBoardPopup.headline')}
                </Text>
                <Text variant="body" as="p" color="foreground.muted">
                    {translate('vessel.insertRemainingOnBoardPopup.description')}
                </Text>
                <Formik initialValues={initialValues} onSubmit={(data) => props.onSubmit({ data })}>
                    <Form>
                        <Box display="flex" flexDirection="column" gap={16}>
                            <FormikTimestampDatePicker
                                name="dateMeasured"
                                label={translate('vessel.insertRemainingOnBoardPopup.dateMeasured')}
                            />

                            <FutureMeasurementDateCallout />

                            {tanks.map((tank) => (
                                <FormikFormattedNumberInput
                                    key={tank.id}
                                    name={`stocks.${tank.id}.value`}
                                    label={
                                        tank.name +
                                        Characters.EN_SPACE +
                                        Characters.MIDDLE_DOT +
                                        Characters.EN_SPACE +
                                        getHumanReadableValue('vesselTankCategory', tank.category)
                                    }
                                    meta={
                                        tank.maxVolume
                                            ? translate('vessel.insertRemainingOnBoardPopup.stockAnnotation', {
                                                  maxVolume: formatNumber({ number: tank.maxVolume }),
                                              })
                                            : null
                                    }
                                    trailingVisual="L"
                                />
                            ))}

                            <Box display="flex" columnGap={8} marginTop={16} justifyContent="end">
                                <Button emphasis="high" type="submit">
                                    {translate('vessel.insertRemainingOnBoardPopup.updateButton')}
                                </Button>
                            </Box>
                        </Box>
                    </Form>
                </Formik>
            </Box>
        </Modal>
    );
};
